import { useEffect, useRef, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { ApiError } from '../../api/index.ts';
import type { OwnerShopRef, OwnerSummary } from '../../api/types.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { QuietButton } from '../../components/QuietButton.tsx';
import { actionError } from '../../components/actionError.ts';
import { useFullScreenDialog } from '../../hooks/useFullScreenDialog.ts';

export interface OwnerShopsDialogProps {
  open: boolean;
  owner: OwnerSummary | null;
  onClose: () => void;
  /** Called after every successful link/unlink so the parent's owner rows stay current. */
  onSaved: () => void;
}

export function OwnerShopsDialog({
  open,
  owner,
  onClose,
  onSaved,
}: Readonly<OwnerShopsDialogProps>): React.JSX.Element | null {
  const { api } = useAuth();
  const fullScreen = useFullScreenDialog();

  const [linkedShops, setLinkedShops] = useState<OwnerShopRef[]>([]);
  const [availableShops, setAvailableShops] = useState<OwnerShopRef[] | null>(null);
  const [pickerForbidden, setPickerForbidden] = useState(false);
  const [selectedShopId, setSelectedShopId] = useState<number | ''>('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open || !owner) return;
    setLinkedShops(owner.shops);
    setAvailableShops(null);
    setPickerForbidden(false);
    setSelectedShopId('');
    setError('');
    setBusy(false);
    submittingRef.current = false;

    let cancelled = false;
    api.shops
      .list()
      .then((res) => {
        if (cancelled) return;
        setAvailableShops(res.shops.map((s) => ({ id: s.id, shop_code: s.shop_code, name: s.name })));
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 403) {
          setPickerForbidden(true);
        } else {
          setError(actionError(e));
        }
      });
    return () => {
      cancelled = true;
    };
    // Reset keyed on the owner's identity, not the whole parent-refreshed
    // object -- refreshLinked() below is this dialog's own source of truth
    // once it is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, owner?.id, api]);

  if (!owner) return null;

  const refreshLinked = async (): Promise<void> => {
    const fresh = await api.owners.get(owner.id);
    setLinkedShops(fresh.shops);
  };

  const unlink = async (shopId: number): Promise<void> => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    setError('');
    try {
      await api.owners.unlinkShop(owner.id, shopId);
      await refreshLinked();
      onSaved();
    } catch (e) {
      setError(actionError(e));
    } finally {
      setBusy(false);
      submittingRef.current = false;
    }
  };

  const link = async (): Promise<void> => {
    if (submittingRef.current || selectedShopId === '') return;
    submittingRef.current = true;
    setBusy(true);
    setError('');
    try {
      await api.owners.linkShop(owner.id, Number(selectedShopId));
      await refreshLinked();
      onSaved();
      setSelectedShopId('');
    } catch (e) {
      setError(actionError(e));
    } finally {
      setBusy(false);
      submittingRef.current = false;
    }
  };

  const linkedIds = new Set(linkedShops.map((s) => s.id));
  const pickable = (availableShops ?? []).filter((s) => !linkedIds.has(s.id));

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" fullScreen={fullScreen}>
      <DialogTitle>
        Manage shops -- {owner.first_name} {owner.last_name}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Linked shops
            </Typography>
            {linkedShops.length === 0 && (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                No shops linked yet.
              </Typography>
            )}
            <List dense disablePadding>
              {linkedShops.map((s) => (
                <ListItem
                  key={s.id}
                  disableGutters
                  secondaryAction={
                    <QuietButton size="small" disabled={busy} onClick={() => void unlink(s.id)}>
                      Unlink
                    </QuietButton>
                  }
                >
                  <ListItemText primary={`${s.shop_code} -- ${s.name}`} />
                </ListItem>
              ))}
            </List>
          </Box>

          {pickerForbidden ? (
            <Alert severity="info" sx={{ fontWeight: 600 }}>
              Shop directory requires the View shops permission -- ask an admin to link shops.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <FormControl fullWidth size="small" disabled={busy || availableShops === null}>
                <InputLabel id="owner-shop-add-label">Add shop</InputLabel>
                <Select<number | ''>
                  labelId="owner-shop-add-label"
                  label="Add shop"
                  value={selectedShopId}
                  onChange={(e) => setSelectedShopId(e.target.value === '' ? '' : Number(e.target.value))}
                  inputProps={{ 'aria-label': 'owner-shop-add' }}
                >
                  {pickable.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.shop_code} -- {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button disabled={busy || selectedShopId === ''} onClick={() => void link()}>
                Link
              </Button>
            </Box>
          )}

          {error && (
            <Alert role="alert" severity="error" sx={{ fontWeight: 700 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <QuietButton onClick={onClose} disabled={busy}>
          Close
        </QuietButton>
      </DialogActions>
    </Dialog>
  );
}

export default OwnerShopsDialog;
